import React, { useState } from 'react';
import Modal from '@material-ui/core/Modal';
import { LBButton, LBGithubIcon, LBXIcon } from '@linearb/buzz';
import { getGitHubAppInstallUrl } from '../../common/services/sensors.service';

interface GitHubAppInstallationModalProps {
  visible: boolean;
  onClose: () => void;
}

export const GitHubAppInstallationModal: React.FC<
  GitHubAppInstallationModalProps
> = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);

  const handleInstall = async () => {
    try {
      setLoading(true);

      // Get installation URL from backend
      const response = await getGitHubAppInstallUrl();
      // @ts-ignore
      const { install_url } = response;

      // Redirect to GitHub for app installation
      window.location.href = install_url;
    } catch (error) {
      // Error getting GitHub App installation URL - handled by UI state
      setLoading(false);
      // Could add error notification here
    }
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      className='flex items-center justify-center p-4'
    >
      <div className='bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 outline-none relative'>
        {/* Close button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors'
          aria-label='Close modal'
        >
          <LBXIcon size={20} className='text-gray-500' />
        </button>

        {/* Header with icon */}
        <div className='text-center pt-8 pb-6 px-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-900 to-gray-700 rounded-2xl mb-6'>
            <LBGithubIcon size={32} className='text-white' />
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-3'>
            Connect GitHub
          </h2>
          <p className='text-gray-600 text-base leading-relaxed'>
            Install the LinearB GitHub App to unlock powerful automation and
            analytics for your repositories.
          </p>
        </div>

        {/* Benefits list */}
        <div className='px-8 pb-6'>
          <div className='space-y-3'>
            <div className='flex items-start space-x-3'>
              <div className='w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0'>
                <div className='w-2 h-2 rounded-full bg-green-500'></div>
              </div>
              <span className='text-gray-700 text-sm'>
                Automatically collect repository data and metrics
              </span>
            </div>
            <div className='flex items-start space-x-3'>
              <div className='w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0'>
                <div className='w-2 h-2 rounded-full bg-blue-500'></div>
              </div>
              <span className='text-gray-700 text-sm'>
                Enable advanced analytics and team insights
              </span>
            </div>
            <div className='flex items-start space-x-3'>
              <div className='w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mt-0.5 flex-shrink-0'>
                <div className='w-2 h-2 rounded-full bg-purple-500'></div>
              </div>
              <span className='text-gray-700 text-sm'>
                Set up powerful automation workflows
              </span>
            </div>
            <div className='flex items-start space-x-3'>
              <div className='w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center mt-0.5 flex-shrink-0'>
                <div className='w-2 h-2 rounded-full bg-orange-500'></div>
              </div>
              <span className='text-gray-700 text-sm'>
                Track team performance and delivery metrics
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className='px-8 pb-8'>
          <LBButton
            onClick={handleInstall}
            disabled={loading}
            size='lg'
            className='w-full mb-4'
          >
            {loading ? 'Redirecting to GitHub...' : 'Install GitHub App'}
          </LBButton>

          <p className='text-xs text-gray-500 text-center leading-relaxed'>
            You'll be redirected to GitHub to authorize the LinearB app for your
            organization or repositories.
          </p>
        </div>
      </div>
    </Modal>
  );
};
